import { useState } from 'react'
import { useUserSession } from 'features/session/context'

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  useTheme,
  useMediaQuery
} from '@mui/material'

import { ErrorBlock } from 'components/controls/errorInfo'
import { Loading } from 'components/controls/loading'

import { KojinResult } from 'interface/kojin'
import { AuthorResult } from 'interface/author'

import { KojinDownloadDialog } from 'features/kojin/dialog/download'
import { SisetuPreviewDialog } from 'features/sisetu/dialog/preview'
import { ArticlePreviewDialog } from 'features/article/dialog/preview'
import { TrialPreviewDialog } from 'features/trial/dialog/preview'

import { useKojinPreview } from '../../preview/container'
import { KojinHeader } from '../../preview/components/header'
import { KojinPreviewTab } from '../../preview/components/tab'
import { KojinBasicPage } from '../../preview/components/basicPage'
import { KojinEvaluationPage } from '../../preview/components/evaluationPage'
import { KojinSummaryPage } from '../../preview/components/summaryPage'
import { KojinJournalPage } from '../../preview/components/journalPage'
import { KojinCareNetPage } from '../../preview/components/careNetPage'

import { useKojinSummary } from '../../preview/components/summaryPage/components/kojinSummary'

const previewDialogTitleId = 'kojin-preview-dialog-title'

const KojinPreviewTabPageProps = (value: string) => ({
  role: 'tabpanel',
  id: `kojin-preview-${value}`,
  'aria-labelledby': `kojin-preview-tabpanel-${value}`,
})

type KojinPreviewDialogProps = {
  kojinList?: KojinResult[]
  kojinCode?: string | null
  kojin?: KojinResult | null
  authorId?: number | null
  author?: AuthorResult | null
  onSisetuOpen?: (sisetuCode: string | null) => void
  onKojinOpen?: (kojinCode: string | null) => void
  onAuthorOpen?: (authorId: number | null) => void
  onArticleOpen?: (articleId: number | null) => void
  onTrialOpen?: (trialId: number | null) => void
  onChangeEvaluation?: (kojinCode: string) => void
  onClose: () => void
}
export const KojinPreviewDialog = ({
  kojinList,
  kojinCode: defaultKojinCode,
  kojin: defaultKojin,
  authorId: defaultAuthorId,
  author: defaultAuthor,
  onSisetuOpen: onSisetuOpenReply,
  onKojinOpen: onKojinOpenReply,
  onAuthorOpen: onAuthorOpenReply,
  onArticleOpen: onArticleOpenReply,
  onTrialOpen: onTrialOpenReply,
  onChangeEvaluation,
  onClose
}: KojinPreviewDialogProps) => {
  const theme = useTheme()
  const isSmallMedia = useMediaQuery(theme.breakpoints.down("sm"))
  const { isCareNet } = useUserSession()

  const [tabName, setTabName] = useState<string>('basic')

  const container = useKojinPreview({
    kojinList,
    defaultKojinCode,
    defaultKojin,
    defaultAuthorId,
    defaultAuthor,
    onSisetuOpenReply,
    onKojinOpenReply,
    onAuthorOpenReply,
    onArticleOpenReply,
    onTrialOpenReply,
    onChangeEvaluation,
    onClose
  })
  const {
    loading, error, 
    modal, closeModal,
    kojin, articleCount,
    /*onSisetuOpen,*/ selectedSisetuCode,
    /*onKojinOpen,*/ selectedKojinCode,
    /*onAuthorOpen,*/ selectedAuthorId,
    /*onArticleOpen,*/ selectedArticleId, articleList,
    /*onTrialOpen,*/ selectedTrialId, trialList,
    downloading, onDownload
  } = container

  const summary = useKojinSummary(kojin)

  return (
    <Dialog
      open
      fullScreen={isSmallMedia}
      maxWidth='lg'
      fullWidth
      onClose={() => onClose()}
      aria-labelledby={previewDialogTitleId}
    >
      {loading && <Loading />}
      <ErrorBlock error={error}/>

      <DialogTitle
        id={previewDialogTitleId}
        sx={{ padding: 1 }}
      >
				<KojinHeader container={container} tabName={tabName}/>
      </DialogTitle>

      <Tabs
        value={tabName}
        onChange={(_, newValue) => setTabName(newValue)}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          paddingX: isSmallMedia ? 0 : '1em'
        }}
        //variant="scrollable"
        //scrollButtons="auto"
        //allowScrollButtonsMobile
        aria-label="kojin-preview-tabs"
      >
        <KojinPreviewTab label="基本情報" value="basic" />
        {container.evaluationForm && ( 
          <KojinPreviewTab label="評価" value="evaluation" />
        )}
        {summary.isValid && (
          <KojinPreviewTab label="サマリー" value="summary" />
        )}
        {(articleCount > 0) && (
          <KojinPreviewTab
            label="論文"
            value="journal"
            badge={articleCount}
          />
        )}
        {isCareNet && kojin?.personalId && (
          <KojinPreviewTab
            label="閲覧履歴"
            value="carenet"
            disabled={kojin?.personalId ? false : true}
            badge={kojin?.mrpStats?.rank.name}
          />
        )}
      </Tabs>

      <DialogContent
        sx={{
          padding: 0,
          height: '60vh',
          position: 'relative',
        }}
      >
        {tabName === 'basic' && (
          <KojinBasicPage
            key="kojin-preview-basic"
            {...KojinPreviewTabPageProps('basic')}
            container={container}
            isSmallMedia={isSmallMedia}
          />
        )}
        {tabName === 'evaluation' && (
          <KojinEvaluationPage
            key="kojin-preview-evaluation"
            {...KojinPreviewTabPageProps('evaluation')}
            container={container}
          />
        )}
        {tabName === 'summary' && (
          <KojinSummaryPage
            key="kojin-preview-summary"
            {...KojinPreviewTabPageProps('summary')}
            container={summary}
          />
        )}
        {tabName === 'journal' && (
          <KojinJournalPage
            key="kojin-preview-journal"
            {...KojinPreviewTabPageProps('journal')}
            container={container}
            isSmallMedia={isSmallMedia}
          />
        )}
        {tabName === 'carenet' && (
          <KojinCareNetPage
            key="kojin-preview-carenet"
            {...KojinPreviewTabPageProps('carenet')}
            container={container}
            isSmallMedia={isSmallMedia}
          />
        )}
      </DialogContent>

      {(modal === 'download' && kojin) ? (
        <KojinDownloadDialog
          downloading={downloading}
          defaultTitle={kojin.isiName ?? kojin.isiNameKana ?? 'No name'}
          onDownload={onDownload}
          onClose={closeModal}
        />
      ) : (selectedKojinCode || selectedAuthorId) ? (
        <KojinPreviewDialog
          kojinCode={selectedKojinCode}
          authorId={selectedAuthorId}
          onClose={closeModal}
        />
      ) : selectedSisetuCode ? (
        <SisetuPreviewDialog
          sisetuCode={selectedSisetuCode}
          onClose={closeModal}
        />
      ) : selectedArticleId ? (
        <ArticlePreviewDialog
          articleList={articleList}
          articleId={selectedArticleId}
          targetKojinCode={kojin?.kojinCode}
          targetAuthorId={kojin?.authorId}
          onClose={closeModal}
        />
      ) : selectedTrialId ? (
        <TrialPreviewDialog
          trialList={trialList}
          trialId={selectedTrialId}
          targetKojinCode={kojin?.kojinCode}
          onClose={closeModal}
        />
      ) : <></>}
    </Dialog>
  )
}
